import { Dayjs } from 'dayjs';
import { TABLE_LABELS, TEAM_ASSIGNED_TRAININGS_LABELS } from '../../../../lib/constants/labelKeys';
import { API_DATE_FORMAT } from '../../../../lib/constants/globals';
import { Label } from '../../../common/Label/Label';
import PureTable from '../../../common/PureTable/PureTable';
import styles from './TeamAssignedTrainings.module.scss';
import { getDecodedHashParams } from '../../../../lib/utils/hashUtils';
import { FilteredTeamAssignedTrainingsQueryParams } from './TeamAssignedTrainings.types';
import { isNil } from '../../../../lib/utils/isNil';
import { CourseTileOrder, CourseType as CourseTileType } from '../../base/TrainingSummaryView/CourseTile/CourseTile';
import LitmosTrainingLink, { getCustomLinkElement } from '../../base/LearningLink/LearningLink';
import { Api } from '../../../../lib/types/apiSpec';
import { getTrainingType } from '../../../../lib/utils/trainings/typesChecking';

const DEFAULT_TILES_ORDER: CourseTileOrder = ['assigned', 'completed', 'in-progress', 'overdue', 'not-started'];

export const INITIAL_TEAM_ASSIGNED_TRAININGS_STATE = (today?: Dayjs) => ({
  page: '1',
  sortBy: Api.SortBy.name,
  sortDirection: Api.SortDirection.asc,
  ...(today && {
    since: today.subtract(30, 'days').format(API_DATE_FORMAT),
    to: today.format(API_DATE_FORMAT),
  }),
  search: '',
});

export const dataToSummaryTiles = (
  id: string,
  summaryData?: Api.Summary,
  summaryItemsOrder: CourseTileOrder = DEFAULT_TILES_ORDER,
) =>
  summaryItemsOrder.flatMap(item => {
    switch (item) {
      case 'assigned':
        return {
          id: `assigned-${id}`,
          type: 'assigned' as CourseTileType,
          value: summaryData?.totalAssigned ?? (isNil(summaryData) ? 0 : undefined),
        };
      case 'completed':
        return {
          id: `completed-${id}`,
          type: 'completed' as CourseTileType,
          value: summaryData?.totalCompleted ?? (isNil(summaryData) ? 0 : undefined),
        };
      case 'in-progress':
        return {
          id: `in-progress-${id}`,
          type: 'in-progress' as CourseTileType,
          value: summaryData?.totalInProgress ?? (isNil(summaryData) ? 0 : undefined),
        };
      case 'overdue':
        return {
          id: `overdue-${id}`,
          type: 'overdue' as CourseTileType,
          value: summaryData?.totalOverdue ?? (isNil(summaryData) ? 0 : undefined),
        };
      case 'not-started':
        return {
          id: `not-started-${id}`,
          type: 'not-started' as CourseTileType,
          value: summaryData?.totalNotStarted ?? (isNil(summaryData) ? 0 : undefined),
        };
      default:
        return [];
    }
  });

export const assignedCoursesSortValues = (headings: string[]) =>
  headings.flatMap(heading => [
    {
      value: `${heading}|${Api.SortDirection.asc}`,
      labelKey: TABLE_LABELS.SORT_BY_ITEM(heading),
    },
    {
      value: `${heading}|${Api.SortDirection.desc}`,
      labelKey: TABLE_LABELS.SORT_BY_ITEM(heading),
    },
  ]);

export const adaptCourseSummariesToTableData =
  (hideOverdue: boolean) =>
  ({
    id = '',
    totalCompleted = 0,
    totalOverdue,
    name = '',
    totalInProgress = 0,
    totalNotStarted = 0,
    type,
    ...props
  }: Api.TeamCourseSummary) => {
    return {
      ...props,
      id,
      name: (
        <LitmosTrainingLink
          className={styles.courseTitleLink}
          type={getTrainingType(type)}
          id={id}
          name={name}
          ContentElement={getCustomLinkElement({ asButton: false, label: name })}
        />
      ),
      totalCompleted,
      totalInProgress,
      totalNotStarted,
      ...(hideOverdue
        ? null
        : {
            totalOverdue: totalOverdue ? (
              <PureTable.Badge key={`overdue|${totalOverdue}`}>
                {totalOverdue}&nbsp;
                <Label name={TEAM_ASSIGNED_TRAININGS_LABELS.OVERDUE_SUFFIX} />
              </PureTable.Badge>
            ) : (
              <Label key={`overdue-${id}`} name={TABLE_LABELS.VALUE_NOT_AVAILABLE} />
            ),
          }),
    };
  };

export const getInitialDataFromHashParams = ({
  hash,
  pageSize,
  today,
}: {
  hash: string;
  pageSize: number;
  today?: Dayjs;
}): FilteredTeamAssignedTrainingsQueryParams => {
  const decodedHashParams = getDecodedHashParams(hash);
  const { page, search, since, sortDirection, sortBy, to } = INITIAL_TEAM_ASSIGNED_TRAININGS_STATE(today);

  return {
    page: parseFloat(decodedHashParams.get(Api.Param.Page) ?? page),
    sortBy: decodedHashParams.get(Api.Param.SortBy) ?? sortBy,
    sortDirection: decodedHashParams.get(Api.Param.SortDirection) ?? sortDirection,
    ...(today && { since: decodedHashParams.get(Api.Param.Since) ?? since, to: decodedHashParams.get('to') ?? to }),
    search: decodedHashParams.get(Api.Param.Search) ?? search,
    pageSize: decodedHashParams.get(Api.Param.PageSize) ?? pageSize,
    teamId: decodedHashParams.get(Api.Param.TeamId),
  } as FilteredTeamAssignedTrainingsQueryParams;
};
